import { Box, FormControl, MenuItem, Stack, Typography } from "@mui/material";
import { get } from "lodash";
import { FC, useEffect, useState } from "react";
import CdsFeedbackTextField from "../../pages/health/encounters/cards/vitals/components/CdsFeedbackTextField";
import CdsArrowTooltip from "../../pages/health/encounters/cards/vitals/components/cdsArrowTooltip";
import { Feedback } from "../../pages/health/encounters/cards/vitals/types/commonCDSTypes";
import { createCodeableConcept } from "../../utils/DataUtils";

type ScoreOption = {
  code: string;
  display: string;
  value: number;
};

const eyeOptions: ScoreOption[] = [
  { code: "LA6556-0", display: "Opens eyes spontaneously", value: 4 },
  { code: "LA6555-2", display: "Opens eyes in response to voice", value: 3 },
  { code: "LA6554-5", display: "Opens eyes in response to pain", value: 2 },
  { code: "LA6553-7", display: "No eye opening", value: 1 },
];

const motorOptions: ScoreOption[] = [
  { code: "LA6567-7", display: "Obeys commands", value: 6 },
  { code: "LA6566-9", display: "Localizing pain", value: 5 },
  { code: "LA6565-1", display: "Withdrawal from pain", value: 4 },
  { code: "LA6564-4", display: "Flexion to pain", value: 3 },
  { code: "LA6563-6", display: "Extension to pain", value: 2 },
  { code: "LA6562-8", display: "No motor response", value: 1 },
];

const verbalOptions: ScoreOption[] = [
  { code: "LA6561-0", display: "Oriented", value: 5 },
  { code: "LA6560-2", display: "Confused", value: 4 },
  { code: "LA6559-4", display: "Inappropriate words", value: 3 },
  { code: "LA6558-6", display: "Incomprehensible sounds", value: 2 },
  { code: "LA6557-8", display: "No verbal response", value: 1 },
];

const GCS_LOINC_CODES = {
  EYE: { code: "9267-6", display: "Glasgow coma score eye" },
  MOTOR: { code: "9268-4", display: "Glasgow coma score motor" },
  VERBAL: { code: "9270-0", display: "Glasgow coma score verbal" },
  //   TOTAL: "",
};

type GCSProps = {
  value: any;
  disabled?: boolean;
  onChange?: (value) => void;
};

export const getFeedbackForScore = (score: number): Feedback | undefined => {
  if (score >= 13 && score <= 15) {
    return {
      type: "success",
      message:
        "Normal (13-15): Full alertness, oriented to person, place, and time",
    };
  }
  if (score >= 9 && score <= 12) {
    return {
      type: "warning",
      message:
        "Moderate (9-12): Increased risk of complications, may require close monitoring",
    };
  }
  if (score >= 3 && score <= 8) {
    return {
      type: "error",
      message:
        "Severe (3-8): Coma, may require intubation and mechanical ventilation",
    };
  }
  return undefined;
};

const getScoreColor = (feedbackType?: string): string => {
  switch (feedbackType) {
    case "error":
      return "red";
    case "warning":
      return "orange";
    case "success":
      return "green";
    default:
      return "inherit";
  }
};

const createNewCoding = (option: ScoreOption) => [
  {
    system: "http://example.org/codes",
    code: option.value.toString(),
    display: option.display,
  },
  {
    extension: [
      {
        url: "http://hl7.org/fhir/StructureDefinition/itemWeight",
        valueDecimal: option.value,
      },
    ],
    system: "http://loinc.org",
    code: option.code,
    display: option.display,
  },
];

export const GlasgowComaScale: FC<GCSProps> = ({
  value,
  disabled,
  onChange,
}: GCSProps) => {
  const [eyeScore, setEyeScore] = useState<string>("");
  const [motorScore, setMotorScore] = useState<string>("");
  const [verbalScore, setVerbalScore] = useState<string>("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (value && Array.isArray(value.component)) {
      value.component?.forEach((component: any) => {
        const componentCode = get(component, "code.coding[0].code");
        const selectedValue = get(component, "value.coding[0].code");

        switch (componentCode) {
          case GCS_LOINC_CODES.EYE.code:
            setEyeScore(selectedValue);
            break;
          case GCS_LOINC_CODES.MOTOR.code:
            setMotorScore(selectedValue);
            break;
          case GCS_LOINC_CODES.VERBAL.code:
            setVerbalScore(selectedValue);
            break;
        }
      });
    }
  }, [value]);

  const handleScoreChange = (
    scoreType: "eye" | "motor" | "verbal",
    newValue: string,
  ) => {
    const scoreSetters = {
      eye: setEyeScore,
      motor: setMotorScore,
      verbal: setVerbalScore,
    };

    scoreSetters[scoreType](newValue);

    const options = {
      eye: eyeOptions,
      motor: motorOptions,
      verbal: verbalOptions,
    };

    const option = options[scoreType].find(
      (opt) => opt.value === Number(newValue),
    );

    if (!option) return;

    const updatedObservation = { ...value };
    const componentIndex = updatedObservation.component.findIndex(
      (comp: any) =>
        comp.code.coding[0].code ===
        GCS_LOINC_CODES[scoreType.toUpperCase()].code,
    );

    const newCoding = createNewCoding(option);

    if (componentIndex !== -1) {
      updatedObservation.component[componentIndex].value.coding = newCoding;
    } else {
      updatedObservation.component.push({
        code: createCodeableConcept(
          "http://loinc.org",
          GCS_LOINC_CODES[scoreType.toUpperCase()].code,
          GCS_LOINC_CODES[scoreType.toUpperCase()].display,
        ),
        value: {
          coding: newCoding,
          text: `${option.value} (${option.display})`,
        },
      });
    }

    const scores = {
      eye:
        eyeOptions.find(
          (opt) =>
            opt.value === Number(scoreType === "eye" ? newValue : eyeScore),
        )?.value || 0,
      motor:
        motorOptions.find(
          (opt) =>
            opt.value === Number(scoreType === "motor" ? newValue : motorScore),
        )?.value || 0,
      verbal:
        verbalOptions.find(
          (opt) =>
            opt.value ===
            Number(scoreType === "verbal" ? newValue : verbalScore),
        )?.value || 0,
    };

    const newTotal = Object.values(scores).reduce(
      (sum, score) => sum + score,
      0,
    );
    updatedObservation.value.value = newTotal;

    onChange(updatedObservation);
  };

  const totalScore = get(value, "value.value", 0);
  const feedback = getFeedbackForScore(totalScore);

  return (
    <Box sx={{ maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1">Glasgow Coma Scale</Typography>
        <Typography
          variant="subtitle1"
          sx={{ ml: 2, color: getScoreColor(feedback?.type) }}
        >
          Score: {totalScore}
        </Typography>
      </Box>
      <Stack
        direction={{ xs: "column", md: "row", lg: "column" }}
        spacing={2}
        sx={{
          width: "100%",
        }}
      >
        <FormControl sx={{ flex: 1 }}>
          <CdsArrowTooltip
            type={feedback?.type}
            feedbackText={feedback?.message}
            openCDS={totalScore > 0 && !menuOpen}
            placement="top"
          >
            <CdsFeedbackTextField
              feedbackType={feedback?.type}
              label="Eyes"
              size="small"
              value={eyeScore}
              select
              disabled={disabled}
              onChange={(e) => handleScoreChange("eye", e.target.value)}
              SelectProps={{
                onOpen: () => setMenuOpen(true),
                onClose: () => setMenuOpen(false),
              }}
            >
              {eyeOptions.map((option) => (
                <MenuItem key={option.code} value={option.value}>
                  {option.display} ({option.value})
                </MenuItem>
              ))}
            </CdsFeedbackTextField>
          </CdsArrowTooltip>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <CdsArrowTooltip
            type={feedback?.type}
            feedbackText={feedback?.message}
            openCDS={totalScore > 0 && !menuOpen}
            placement="top"
          >
            <CdsFeedbackTextField
              feedbackType={feedback?.type}
              label="Motor"
              size="small"
              select
              disabled={disabled}
              value={motorScore}
              onChange={(e) => handleScoreChange("motor", e.target.value)}
              SelectProps={{
                onOpen: () => setMenuOpen(true),
                onClose: () => setMenuOpen(false),
              }}
            >
              {motorOptions.map((option) => (
                <MenuItem key={option.code} value={option.value}>
                  {option.display} ({option.value})
                </MenuItem>
              ))}
            </CdsFeedbackTextField>
          </CdsArrowTooltip>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <CdsArrowTooltip
            type={feedback?.type}
            feedbackText={feedback?.message}
            openCDS={totalScore > 0 && !menuOpen}
            placement="top"
          >
            <CdsFeedbackTextField
              feedbackType={feedback?.type}
              size="small"
              select
              label="Verbal"
              disabled={disabled}
              value={verbalScore}
              onChange={(e) => handleScoreChange("verbal", e.target.value)}
              SelectProps={{
                onOpen: () => setMenuOpen(true),
                onClose: () => setMenuOpen(false),
              }}
            >
              {verbalOptions.map((option) => (
                <MenuItem key={option.code} value={option.value}>
                  {option.display} ({option.value})
                </MenuItem>
              ))}
            </CdsFeedbackTextField>
          </CdsArrowTooltip>
        </FormControl>
      </Stack>
    </Box>
  );
};
